import { useState } from "react";
import { Play, ArrowRight, ExternalLink } from "lucide-react";
import electusLogo from "@/assets/partner-videoproduktion-aachen.png";
import electusPreview from "@/assets/partner-recruiting-video-aachen.jpg";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { useIsMobile } from "@/hooks/use-mobile";

const Philosophy = () => {
  const [normalPlayback, setNormalPlayback] = useState(false);
  const [loadVideo, setLoadVideo] = useState(false);
  const isMobile = useIsMobile();

  const handleVideoClick = () => {
    setLoadVideo(true);
    setNormalPlayback(true);
  };

  return (
    <section className="py-12 md:py-20 lg:py-24 bg-gradient-to-b from-background via-background to-background relative overflow-hidden">
      {/* Subtle AI Background Elements */}
      <div className="absolute inset-0 opacity-[0.06]">
        {/* Glowing Orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent rounded-full blur-[120px]"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent rounded-full blur-[120px]"></div>
      </div>
      
      {/* Circuit Pattern */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: `
          linear-gradient(90deg, hsl(var(--accent)) 1px, transparent 1px),
          linear-gradient(0deg, hsl(var(--accent)) 1px, transparent 1px)
        `,
        backgroundSize: '60px 60px'
      }}></div>
      
      {/* AI Nodes */}
      <div className="absolute inset-0 opacity-[0.08]">
        <div className="absolute top-1/3 left-1/5 w-3 h-3 bg-accent rounded-full animate-pulse"></div>
        <div className="absolute top-2/3 left-2/5 w-2 h-2 bg-accent rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute top-1/2 right-1/4 w-3 h-3 bg-accent rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-1/3 right-1/3 w-2 h-2 bg-accent rounded-full animate-pulse" style={{ animationDelay: '1.5s' }}></div>
      </div>
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Left: Video */}
          <div className="relative animate-fade-in group">
            <div className="aspect-video rounded-lg overflow-hidden relative shadow-2xl shadow-accent/20 border border-accent/30">
              {/* Thumbnail Fallback - visible on mobile until tapped */}
              <div 
                className={`absolute inset-0 bg-cover bg-center transition-opacity duration-500 ${loadVideo ? 'opacity-0' : 'opacity-100'}`}
                style={{ backgroundImage: `url(https://vumbnail.com/1137905376.jpg)` }}
              />
              {normalPlayback ? (
                <iframe
                  src="https://player.vimeo.com/video/1137905376?badge=0&player_id=0&app_id=58479&autoplay=1&dnt=1"
                  className="w-full h-full relative z-10"
                  allow="autoplay; fullscreen; picture-in-picture"
                  title="Videoagentur Aachen - Unsere Philosophie"
                />
              ) : (
                <>
                  {/* On mobile: only load iframe after tap; on desktop: always load */}
                  {!isMobile && (
                    <iframe
                      src="https://player.vimeo.com/video/1137905376?autoplay=1&loop=1&muted=1&autopause=0&controls=0&background=1&playsinline=1&dnt=1"
                      className="w-full h-full relative z-10"
                      allow="autoplay; fullscreen; picture-in-picture"
                      title="Imagefilm Aachen - Videoagentur Philosophie"
                    />
                  )}
                  <div 
                    className="absolute inset-0 flex items-center justify-center cursor-pointer group/play z-20"
                    onClick={handleVideoClick}
                  >
                    <div className="bg-background/80 backdrop-blur-sm rounded-full p-6 group-hover/play:bg-background/90 transition-all duration-300 group-hover/play:scale-110">
                      <Play className="w-8 h-8 text-accent fill-accent" />
                    </div>
                  </div>
                </>
              )}
            </div>
            
            {/* Electus Partner Mention */}
            <HoverCard openDelay={200} closeDelay={100}>
              <HoverCardTrigger asChild>
                <a 
                  href="https://electus.de" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-4 mt-6 px-5 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-accent/30 transition-all group/partner"
                >
                  <span className="text-sm text-muted-foreground">Fester Partner</span>
                  <div className="h-4 w-px bg-white/20"></div>
                  <img 
                    src={electusLogo} 
                    alt="Electus Partner - Videoproduktion Aachen" 
                    className="h-6 md:h-7 w-auto object-contain"
                    loading="lazy"
                    decoding="async"
                  />
                  <ArrowRight className="w-4 h-4 text-muted-foreground group-hover/partner:text-accent group-hover/partner:translate-x-1 transition-all" />
                </a>
              </HoverCardTrigger>
              <HoverCardContent 
                className="w-80 bg-background border border-border shadow-xl p-0 overflow-hidden"
                side="bottom"
                align="start"
              >
                <div className="w-full h-40 overflow-hidden">
                  <img 
                    src={electusPreview} 
                    alt="Recruiting Video Aachen - Electus Partner Vorschau" 
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <img 
                      src={electusLogo} 
                      alt="Electus - Partner Videoproduktion Aachen" 
                      className="h-6"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Unser Partner für Recruiting-Kampagnen zur Mitarbeitergewinnung. Wir liefern den Content.
                  </p>
                  <div className="flex items-center gap-2 text-xs text-accent">
                    <ExternalLink className="w-3 h-3" />
                    <span>electus.de besuchen</span>
                  </div>
                </div>
              </HoverCardContent>
            </HoverCard>
          </div>

          {/* Right: Content */}
          <div className="space-y-4 md:space-y-6 animate-fade-in">
            <div className="w-12 md:w-16 h-1 bg-accent"></div>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-normal tracking-tight">
              Unsere<br />Philosophie
            </h2>
            
            <div className="space-y-3 md:space-y-4 text-sm md:text-base text-muted-foreground leading-relaxed">
              <p>
                JC Productions ist auf echten Filmsets entstanden – nicht im Labor.
                Wir wissen, wie ein Film sich anfühlen muss, damit er wirkt:
                <span className="text-foreground font-medium"> Story, Emotion, Timing und Authentizität.</span>
              </p>
              
              <p>
                Genau deshalb sehen unsere KI-Produktionen so real aus.
                Sie werden mit dem Blick von Filmemachern erstellt, nicht von Programmierern.
              </p>
              
              <p className="text-foreground text-base md:text-lg font-medium">
                Wir erzählen Geschichten, die berühren.<br />
                Egal ob klassisch gedreht oder KI-basiert.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Philosophy;
